// Earth generator (ERT-2). PURE + deterministic (GEN-1): the same tiles + scale
// yield identical chunks on the main thread, the gen worker, and the server. Earth
// stamps geometry through the EXISTING Atlas rasterizer (ERT-4) — it converts the
// decoded TIL tiles into an `IntermediateCity` + a `DemTile`, then drives an
// `AtlasGenerator` widened with the Earth Y-range, a sea-level datum, and a
// bounded-extent ocean. Imports are one-way `sim/earth → sim/atlas`; no atlas file
// imports earth, and Atlas call sites stay byte-identical.
import { EARTH } from '../config';
import type { YRange } from '../engine/section';
import type { Chunk } from '../engine/chunk';
import { AtlasGenerator, type AtlasIds } from '../atlas/rasterize';
import type { AtlasElevation } from '../atlas/elevation';
import type { LatLon, DemTilePayload, VectorTilePayload } from './tiles';
import { earthCityFromTiles, earthDemFromTiles } from './index';
import { makeSurfaceOverride, type EarthImagery } from './imagery';

/** The serializable Earth world description that rides `GeneratorConfig.earth` /
 *  `GenInitMsg.earth` (like the Atlas `IntermediateCity`): decoded TIL tiles + the
 *  projection centre, scale, extent, range, and DEM provenance. */
export interface EarthWorld {
  center: LatLon;
  /** Metres per block (ERT-1). */
  scaleM: number;
  /** Bounded extent (m from centre); beyond → sea-level ocean (ERT-6). */
  extentM: number;
  /** DEM provenance — the accuracy badge reads this (ERT-3). */
  demSource: string;
  label: string;
  /** The scale-derived vertical extent (ERT-5). */
  range: YRange;
  vector: VectorTilePayload[];
  dem: DemTilePayload[];
  /** Baked imagery (OVL-4, M43): aligned NAIP tiles / user overlays classified into the
   *  natural-ground surface. Absent ⇒ generation byte-identical to M40 (parity). */
  imagery?: EarthImagery;
}

/** Voxelizes an Earth world through the shared Atlas rasterizer (ERT-2/4). */
export class EarthGenerator {
  private readonly atlas: AtlasGenerator;

  constructor(earth: EarthWorld, ids: AtlasIds, seed: number | string | bigint, opacityByState?: Uint8Array, emissionByState?: Uint8Array) {
    const sizeBlocks = Math.max(1, Math.round(earth.extentM / earth.scaleM));
    const city = earthCityFromTiles(earth.vector, earth.center, earth.scaleM, sizeBlocks, earth.label);
    const dem = earthDemFromTiles(earth.dem);
    // Sea-level datum (ERT-1): y = round(elevM / scaleM), 0 m at block-Y 0, clamped to
    // the Earth range (contrast the Atlas min-elev datum, unchanged there).
    const elevation: AtlasElevation = {
      dem,
      center: earth.center,
      scale: earth.scaleM,
      datum: { baseY: EARTH.SEA_DATUM_Y, datum: 0, clampMinY: earth.range.minY + 1, clampMaxY: earth.range.maxY - 1 },
    };
    this.atlas = new AtlasGenerator(city, ids, seed, opacityByState, emissionByState, elevation, {
      range: earth.range,
      oceanBeyond: { halfBlocks: sizeBlocks, seaY: EARTH.SEA_DATUM_Y, floorDepth: EARTH.OCEAN_FLOOR_DEPTH },
      crustDepth: EARTH.CRUST_DEPTH,
      // Baked imagery repaints the natural-ground surface (OVL-4, M43); absent ⇒ parity.
      surfaceOverride: makeSurfaceOverride(earth.imagery),
    });
  }

  generate(cx: number, cz: number): Chunk {
    return this.atlas.generate(cx, cz);
  }

  /** Deterministic surface height at a world column (one above the ground). */
  terrainHeight(x = 0, z = 0): number {
    return this.atlas.terrainHeight(x, z);
  }
}
